/**
 * Analytics utility for tracking user behavior and SEO performance
 * Integrates with Google Analytics 4 and uses PostHog for session recording
 */

// GA4 Measurement ID - configured via environment variable
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';

// Initialize GA4
export const initGA4 = () => {
  if (typeof window === 'undefined') return;
  
  // Don't initialize if already done or if no measurement ID
  if (window.gtag || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') return;

  // Add GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
    send_page_view: false // We'll send manually for SPA navigation
  });
};

// Track page view
export const trackPageView = (path, title) => {
  // GA4
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
      page_location: window.location.href
    });
  }
  
  // PostHog
  if (window.posthog) {
    window.posthog.capture('$pageview', {
      $current_url: window.location.href,
      path: path,
      title: title
    });
  }
  
  // Track to backend for analytics summary
  trackToBackend('page_view', { path, title });
};

// Track custom events
export const trackEvent = (eventName, properties = {}) => {
  // GA4
  if (window.gtag) {
    window.gtag('event', eventName, properties);
  }
  
  // PostHog
  if (window.posthog) {
    window.posthog.capture(eventName, properties);
  }
  
  // Track to backend
  trackToBackend(eventName, properties);
};

// Predefined event trackers for common actions
export const analytics = {
  // Page view tracking
  pageView: (path, title) => trackPageView(path, title),
  
  // Brand interactions
  brandClick: (brandName, brandId, source) => {
    trackEvent('brand_click', {
      brand_name: brandName,
      brand_id: brandId,
      source: source, // 'explore_brands', 'resources', 'leaderboard', etc.
      timestamp: new Date().toISOString()
    });
  },
  
  // Creator interactions
  creatorClick: (creatorName, creatorId, source) => {
    trackEvent('creator_click', {
      creator_name: creatorName,
      creator_id: creatorId,
      source: source,
      timestamp: new Date().toISOString()
    });
  },
  
  // Navigation events
  navigationClick: (destination, source) => {
    trackEvent('navigation_click', {
      destination: destination,
      source: source,
      timestamp: new Date().toISOString()
    });
  },
  
  // CTA clicks
  ctaClick: (ctaName, ctaLocation) => {
    trackEvent('cta_click', {
      cta_name: ctaName,
      cta_location: ctaLocation,
      timestamp: new Date().toISOString()
    });
  },
  
  // Resource page events
  resourcePageView: (slug, pageType, niche, region) => {
    trackEvent('resource_page_view', {
      slug: slug,
      page_type: pageType,
      niche: niche || 'all',
      region: region || 'global',
      timestamp: new Date().toISOString()
    });
  },
  
  // Funnel tracking
  funnelStep: (stepName, stepNumber, metadata = {}) => {
    trackEvent('funnel_step', {
      step_name: stepName,
      step_number: stepNumber,
      ...metadata,
      timestamp: new Date().toISOString()
    });
  },
  
  // First interaction (engagement signal)
  firstInteraction: (interactionType, element) => {
    // Only track once per session
    if (sessionStorage.getItem('first_interaction_tracked')) return;
    
    // Safely extract element identifier
    let elementId = 'unknown';
    if (typeof element === 'string') {
      elementId = element;
    } else if (element && element.className && typeof element.className === 'string') {
      elementId = element.className.slice(0, 50);
    } else if (element && element.tagName) {
      elementId = element.tagName.toLowerCase();
    } else if (element && typeof element === 'object') {
      elementId = element.toString ? element.toString().slice(0, 50) : 'element';
    }
    
    trackEvent('first_interaction', {
      interaction_type: interactionType,
      element: elementId,
      time_to_interaction: performance.now(),
      timestamp: new Date().toISOString()
    });
    
    sessionStorage.setItem('first_interaction_tracked', 'true');
  },
  
  // Search/Filter actions
  filterApplied: (filterType, filterValue, page) => {
    trackEvent('filter_applied', {
      filter_type: filterType,
      filter_value: filterValue,
      page: page,
      timestamp: new Date().toISOString()
    });
  },
  
  // Bookmark actions
  bookmarkAction: (action, creatorId, creatorName) => {
    trackEvent('bookmark_action', {
      action: action, // 'add' or 'remove'
      creator_id: creatorId,
      creator_name: creatorName,
      timestamp: new Date().toISOString()
    });
  },
  
  // External link clicks
  externalLinkClick: (url, linkType, source) => {
    trackEvent('external_link_click', {
      url: url,
      link_type: linkType, // 'youtube_channel', 'brand_website', etc.
      source: source,
      timestamp: new Date().toISOString()
    });
  }
};

// Track to backend for aggregation
const trackToBackend = async (eventName, properties) => {
  try {
    const API = window.location.origin + '/api';
    await fetch(`${API}/seo-analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: eventName,
        properties: properties,
        timestamp: new Date().toISOString(),
        session_id: getSessionId(),
        referrer: document.referrer,
        user_agent: navigator.userAgent
      })
    });
  } catch (error) {
    // Silently fail - analytics should not break the app
    console.debug('Analytics track failed:', error);
  }
};

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Get traffic source
export const getTrafficSource = () => {
  const referrer = document.referrer;
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check UTM parameters first
  const utmSource = urlParams.get('utm_source');
  if (utmSource) return { type: 'campaign', source: utmSource };
  
  // Check referrer
  if (!referrer) return { type: 'direct', source: 'direct' };
  
  try {
    const referrerUrl = new URL(referrer);
    const referrerHost = referrerUrl.hostname;
    
    // Search engines
    if (referrerHost.includes('google')) return { type: 'organic', source: 'google' };
    if (referrerHost.includes('bing')) return { type: 'organic', source: 'bing' };
    if (referrerHost.includes('duckduckgo')) return { type: 'organic', source: 'duckduckgo' };
    if (referrerHost.includes('yahoo')) return { type: 'organic', source: 'yahoo' };
    
    // Social
    if (referrerHost.includes('twitter') || referrerHost.includes('x.com')) return { type: 'social', source: 'twitter' };
    if (referrerHost.includes('facebook')) return { type: 'social', source: 'facebook' };
    if (referrerHost.includes('linkedin')) return { type: 'social', source: 'linkedin' };
    if (referrerHost.includes('youtube')) return { type: 'social', source: 'youtube' };
    if (referrerHost.includes('reddit')) return { type: 'social', source: 'reddit' };
    
    // Same domain
    if (referrerHost === window.location.hostname) return { type: 'internal', source: 'internal' };
    
    return { type: 'referral', source: referrerHost };
  } catch {
    return { type: 'unknown', source: 'unknown' };
  }
};

export default analytics;
